import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/ui/PageHeader';
import StatusBadge from '@/components/ui/StatusBadge';
import Toast from '@/components/ui/Toast';
import { ApiError } from '@/lib/api';
import { catalogApi, type ProductCard } from '@/lib/catalog';
import { usePerms } from '@/lib/perms';
import { specsApi, type SpecSummary } from '@/lib/specs';
import { aed, dayMonth, shortDate } from '@/lib/format';

/** Карточка товара на данных сервера (этап 1.3): разбор цены (цена партнёра → скидка → себестоимость → наценка по правилу →
 * округление → минимум), партии стока, источник поставки, где используется, добавление в спецификацию. */
export default function ProductPage() {
  const { t } = useTranslation();
  const { sku } = useParams<{ sku: string }>();
  const { can } = usePerms();
  const canEdit = can('catalog', 'write');
  const canSpec = can('specifications', 'write');
  const [product, setProduct] = useState<ProductCard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [optionIndex, setOptionIndex] = useState(-1);
  const [fabric, setFabric] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [specs, setSpecs] = useState<SpecSummary[]>([]);
  const [specId, setSpecId] = useState('');
  const [qty, setQty] = useState(10);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ message: string; detail?: string; tone?: 'success' | 'error' } | null>(null);

  const load = useCallback(async () => {
    if (!sku) return;
    setError(null);
    try {
      setProduct(await catalogApi.product(sku));
    } catch (e) {
      setError((e as ApiError).code);
    }
  }, [sku]);
  useEffect(() => {
    void load();
  }, [load]);

  const openAdd = async () => {
    setShowAdd(true);
    try {
      const list = (await specsApi.list()).filter((s) => s.status === 'Draft' || s.status === 'Quoted');
      setSpecs(list);
      if (list.length && !specId) setSpecId(list[0].id);
    } catch (e) {
      setToast({ message: t('ui.error'), detail: (e as ApiError).code, tone: 'error' });
    }
  };

  const price = useMemo(() => {
    if (!product) return { sale: null as number | null, cost: null as number | null, margin: null as number | null, pct: null as number | null };
    const surcharge = optionIndex >= 0 ? product.options[optionIndex]?.surcharge ?? 0 : 0;
    const base = product.pricing.sale ?? product.pricing.calcSale;
    const cost = product.pricing.cost;
    const sale = base == null ? null : base + surcharge;
    const c = cost == null ? null : cost + surcharge;
    const margin = sale != null && c != null ? sale - c : null;
    return { sale, cost: c, margin, pct: margin != null && sale ? (margin / sale) * 100 : null };
  }, [product, optionIndex]);

  if (error) {
    return (
      <div className="rounded-lg border border-background-200 bg-background-50 p-8 text-center">
        <p className="text-sm font-medium text-foreground-900">{error === 'not_found' ? t('product.notFound') : t('onb.error', { code: error })}</p>
        <Link to="/catalog" className="mt-2 inline-block text-[12px] font-semibold text-primary-700">
          {t('product.backToCatalog')}
        </Link>
      </div>
    );
  }
  if (!product) return <div className="p-8 text-center text-[12.5px] text-foreground-500">{t('ui.loading')}</div>;

  const partner = product.partner;
  const stockTotal = product.stock + product.incomingQty;
  const stockPct = stockTotal > 0 ? (product.stock / stockTotal) * 100 : 0;
  const pr = product.pricing;
  const markupChoices = Array.from(new Set([30, 35, 40, 45, 50, ...(pr.markupOverride != null ? [pr.markupOverride] : [])])).sort((a, b) => a - b);
  const setMarkup = async (v: string) => {
    if (!canEdit) return;
    setBusy(true);
    try {
      setProduct(await catalogApi.patch(product.sku, { markupOverride: v === 'auto' ? null : Number(v) }));
      setToast({ message: t('product.markupSaved') });
    } catch (e) {
      setToast({ message: t('ui.error'), detail: (e as ApiError).code, tone: 'error' });
    } finally {
      setBusy(false);
    }
  };
  const addLine = async () => {
    if (!specId) return;
    setBusy(true);
    try {
      const r = await catalogApi.addToSpec(product.sku, { specificationId: specId, qty, option: optionIndex >= 0 ? product.options[optionIndex]?.label : null });
      setShowAdd(false);
      setToast({ message: t('product.added', { qty, sku: product.sku, spec: r.code }) });
      void load();
    } catch (e) {
      setToast({ message: t('ui.error'), detail: (e as ApiError).code, tone: 'error' });
    } finally {
      setBusy(false);
    }
  };
  const minResaleRule = partner
    ? t(`product.minResaleRule.${pr.minResale != null && partner.minResaleType === 'none' ? 'per_sku' : partner.minResaleType}`, { value: partner.minResaleValue ?? '', price: pr.minResale != null ? aed(pr.minResale, 2) : '—' })
    : t('product.minResaleRule.none');
  const field = (label: string, value: React.ReactNode) => (
    <div key={label} className="min-w-0">
      <dt className="text-[10px] font-medium uppercase tracking-[0.08em] text-foreground-400">{label}</dt>
      <dd className="mt-0.5 truncate text-[12px] text-foreground-900">{value ?? '—'}</dd>
    </div>
  );

  return (
    <div>
      <Link to="/catalog" className="mb-3 inline-flex items-center gap-1 text-[11.5px] font-medium text-foreground-500 transition-colors hover:text-foreground-900">
        <i className="ri-arrow-left-line text-[12px]" /> {t('product.back')}
      </Link>

      <PageHeader
        eyebrow={`${t(`category.${product.category}`, { defaultValue: product.category })} · ${product.sku}`}
        title={product.name}
        description={product.description ?? ''}
        actions={
          <>
            <Link to="/specifications" className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-[12px] font-medium text-foreground-700 transition-colors hover:bg-background-100">
              <i className="ri-file-list-3-line text-[14px]" /> {t('product.viewSpecs')}
            </Link>
            {canSpec && (
              <button type="button" onClick={() => void openAdd()} className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 transition-colors hover:bg-primary-600">
                <i className="ri-add-line text-[14px]" /> {t('product.addToSpec')}
              </button>
            )}
          </>
        }
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="flex flex-col gap-4 xl:col-span-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex h-[260px] w-full shrink-0 items-center justify-center overflow-hidden rounded-md border border-background-200 bg-white sm:w-[300px]">
                {product.image ? <img src={product.image} alt={product.name} className="max-h-full max-w-full object-contain" /> : <span className="text-[11px] text-foreground-400">{t('onb.noImage')}</span>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  {product.stock > 0 ? <StatusBadge tone="primary">{t('product.inStock', { count: product.stock })}</StatusBadge> : <StatusBadge tone="accent">{t('catalog.incoming', { qty: product.incomingQty })}</StatusBadge>}
                  {product.specialOffer && (
                    <StatusBadge tone="accent" dot={false}>
                      {t('product.special')}
                    </StatusBadge>
                  )}
                  {product.source === 'Own' && <StatusBadge tone="secondary">{t('status.Own warehouse')}</StatusBadge>}
                </div>

                <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2">
                  {field(t('product.colour'), product.color)}
                  {field(t('product.base'), product.base)}
                  {field(t('product.supplierArticle'), product.supplierArticle)}
                  {field(t('product.weight'), product.weightKg != null ? `${product.weightKg} kg` : null)}
                  {field(t('product.dims'), product.dims ? `${product.dims} mm` : null)}
                  {field(t('product.cbm'), product.cbm != null ? `${product.cbm.toFixed(3)} m³` : <span className="text-accent-800">{t('product.noCbm')}</span>)}
                  {field(t('product.packing'), product.packing)}
                  {field(t('product.carton'), product.unitsPerCarton != null ? `${product.unitsPerCarton} / ${t('common.units')}` : null)}
                </dl>

                <div className="mt-3 rounded-md border border-background-200 bg-background-100/60 p-2.5">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="font-medium text-foreground-800">{t('product.stockPosition')}</span>
                    <span className="tnum text-foreground-500">{t('product.totalUnits', { count: stockTotal })}</span>
                  </div>
                  <div className="mt-1.5 flex h-2 overflow-hidden rounded-full bg-background-300">
                    <div className="h-full bg-primary-500" style={{ width: `${stockPct}%` }} />
                    <div className="h-full bg-accent-400" style={{ width: `${100 - stockPct}%` }} />
                  </div>
                  <div className="mt-1.5 flex items-center justify-between text-[10.5px]">
                    <span className="flex items-center gap-1 text-foreground-600">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary-500" /> {t('product.nowUnits', { count: product.stock })}
                    </span>
                    <span className="flex items-center gap-1 text-foreground-600">
                      <span className="h-1.5 w-1.5 rounded-full bg-accent-400" />
                      {product.incomingQty > 0 ? t('product.arriving', { qty: product.incomingQty, date: product.incomingEta ? dayMonth(product.incomingEta) : '—' }) : t('product.noInbound')}
                    </span>
                  </div>
                  {product.lots.length > 0 && (
                    <ul className="mt-2 divide-y divide-background-200 border-t border-background-200 pt-1 text-[10.5px]">
                      {product.lots.map((l) => (
                        <li key={l.id} className="flex items-center justify-between py-1">
                          <span className="text-foreground-600">
                            {t(`product.lotKind.${l.kind}`)}
                            {l.eta ? ` · ${dayMonth(l.eta)}` : l.etaText ? ` · ${l.etaText}` : ''}
                          </span>
                          <span className="tnum text-foreground-900">
                            {l.qty} {t('common.units')} · {aed(l.unitPrice, 2)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="text-sm font-semibold text-foreground-950">{t('product.specsTitle')}</h2>
              <dl className="mt-2 divide-y divide-background-200">
                {product.specs.map((s, i) => (
                  <div key={`${s.label}-${i}`} className="flex items-start justify-between gap-3 py-1.5 text-[11.5px]">
                    {s.label ? (
                      <>
                        <dt className="text-foreground-500">{s.label}</dt>
                        <dd className="max-w-[62%] text-right font-medium text-foreground-900">{s.value}</dd>
                      </>
                    ) : (
                      <dd className="text-foreground-900">{s.value}</dd>
                    )}
                  </div>
                ))}
                {product.specs.length === 0 && <p className="py-1.5 text-[11.5px] text-foreground-500">—</p>}
              </dl>
            </div>

            <div className="flex flex-col gap-4">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="text-sm font-semibold text-foreground-950">{t('product.optionsTitle')}</h2>
                <div className="mt-2 space-y-1.5">
                  {product.options.length === 0 && <p className="text-[11.5px] text-foreground-500">—</p>}
                  {product.options.map((o, i) => (
                    <label key={`${o.label}-${i}`} className={`flex cursor-pointer items-center justify-between rounded-md border px-2.5 py-1.5 transition-colors ${optionIndex === i ? 'border-primary-300 bg-primary-50' : 'border-background-200 hover:bg-background-100'}`}>
                      <span className="flex items-center gap-2">
                        <input type="radio" checked={optionIndex === i} onChange={() => setOptionIndex(optionIndex === i ? -1 : i)} onClick={() => optionIndex === i && setOptionIndex(-1)} className="h-3.5 w-3.5 cursor-pointer accent-[oklch(var(--primary-500))]" />
                        <span className="text-[11.5px] text-foreground-800">{o.label}</span>
                      </span>
                      <span className="tnum text-[11px] font-medium text-foreground-600">{o.surcharge > 0 ? `+ ${aed(o.surcharge)}` : t('common.included')}</span>
                    </label>
                  ))}
                </div>

                {product.fabrics.length > 0 && (
                  <>
                    <p className="mt-3 text-[10px] font-medium uppercase tracking-[0.08em] text-foreground-400">{t('product.swatches')}</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      {product.fabrics.map((f, i) => (
                        <button key={f.name} type="button" onClick={() => setFabric(i)} title={f.name} className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-1.5 py-0.5 text-[10.5px] transition-colors ${fabric === i ? 'border-primary-400 bg-primary-50 text-primary-800' : 'border-background-200 text-foreground-600'}`}>
                          <span className="h-3 w-3 rounded-full border border-background-300" style={{ backgroundColor: f.tone }} />
                          {f.name}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <div className="flex items-center gap-1.5">
                  <i className="ri-lock-2-line text-[12px] text-foreground-400" />
                  <h2 className="text-sm font-semibold text-foreground-950">{t('product.supplyTitle')}</h2>
                  <span className="rounded bg-background-100 px-1.5 py-0.5 text-[9.5px] font-medium text-foreground-500">{t('product.internalOnly')}</span>
                </div>
                <div className="mt-2 space-y-1.5 text-[11.5px]">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-foreground-500">{t('product.partner')}</span>
                    <span className="max-w-[62%] truncate text-right font-medium text-foreground-900">{partner ? partner.name : t('product.noPartner')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-foreground-500">{t('product.contract')}</span>
                    <StatusBadge tone={partner?.contractStatus === 'Signed' ? 'primary' : 'accent'}>{partner ? t(`status.${partner.contractStatus}`, { defaultValue: partner.contractStatus }) : t('status.Own warehouse')}</StatusBadge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-foreground-500">{t('product.lastList')}</span>
                    <span className="tnum text-foreground-900">{partner?.lastPriceList ? shortDate(partner.lastPriceList.slice(0, 10)) : '—'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-foreground-500">{t('product.reservation')}</span>
                    <span className="max-w-[62%] text-right text-foreground-900">{partner ? t('product.reservationDays', { count: partner.reservationPeriodDays }) : '—'}</span>
                  </div>
                </div>
                {partner && (
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <Link to={`/partners/${partner.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary-700 hover:text-primary-800">
                      {t('product.openPartner')} <i className="ri-arrow-right-line text-[11px]" />
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="flex items-baseline justify-between gap-2">
              <div>
                <h2 className="text-sm font-semibold text-foreground-950">{t('product.logisticsTitle')}</h2>
                <p className="mt-0.5 text-[11px] text-foreground-500">{t('product.logisticsSub')}</p>
              </div>
              {product.incoterm && <span className="rounded bg-background-100 px-1.5 py-0.5 text-[9.5px] font-medium text-foreground-500">{product.incoterm}</span>}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-background-200 pt-3 sm:grid-cols-4">
              {[
                [t('product.weight'), product.weightKg != null ? `${product.weightKg} kg` : null],
                [t('product.dims'), product.dims],
                [t('product.cbm'), product.cbm != null ? `${product.cbm.toFixed(3)} m³` : null],
                [t('product.unitsPerCarton'), product.unitsPerCarton != null ? String(product.unitsPerCarton) : null],
                [t('product.leadTime'), product.leadTime],
                [t('product.origin'), product.origin],
                [t('product.incoterm'), product.incoterm],
                [t('product.hsCode'), product.hsCode],
                [t('product.minOrder'), product.minOrder != null ? `${product.minOrder} ${t('common.units')}` : null],
                [t('product.packing'), product.packing],
                [t('cabinet.warehouse'), partner ? [partner.warehouse, partner.city].filter(Boolean).join(', ') : 'Al Quoz, Dubai'],
                [t('product.supplierArticle'), product.supplierArticle],
              ].map(([label, value]) => (
                <div key={label as string} className="min-w-0">
                  <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-foreground-400">{label}</p>
                  <p className="mt-0.5 truncate text-[12px] font-medium text-foreground-900">{value ?? '—'}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 border-t border-background-200 pt-2.5 text-[10.5px] text-foreground-400">{t('product.cubeNote')}</p>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-foreground-950">{t('product.pricingTitle')}</h2>
              <span className="rounded bg-background-100 px-1.5 py-0.5 text-[9.5px] font-medium text-foreground-500">AED</span>
            </div>

            <div className="mt-3 space-y-2 text-[12px]">
              <div className="flex items-center justify-between">
                <span className="text-foreground-500">{t('product.partnerPrice')}</span>
                <span className="tnum text-foreground-800">{pr.partnerPrice != null ? aed(pr.partnerPrice) : '—'}</span>
              </div>
              {pr.oldStockPrice != null && (
                <div className="flex items-center justify-between">
                  <span className="text-foreground-500">{t('product.oldStockPrice')}</span>
                  <span className="tnum text-foreground-800">{aed(pr.oldStockPrice)}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-foreground-500">{t('product.discount', { value: pr.discountPct > 0 ? `${pr.discountPct}%` : '—' })}</span>
                <span className="tnum text-rose-600">{pr.discountPct > 0 && pr.partnerPrice != null && pr.cost != null ? `− ${aed((pr.oldStockPrice ?? pr.partnerPrice) - pr.cost, 2)}` : '—'}</span>
              </div>
              <div className="flex items-center justify-between rounded bg-background-100 px-2 py-1.5">
                <span className="font-medium text-foreground-800">{t('product.cost')}</span>
                <span className="tnum font-semibold text-foreground-950">{pr.cost != null ? aed(pr.cost, 2) : '—'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-foreground-500">
                  {t('product.markup')}
                  <select
                    value={pr.markupOverride != null ? String(pr.markupOverride) : 'auto'}
                    disabled={!canEdit || busy}
                    onChange={(e) => void setMarkup(e.target.value)}
                    aria-label={t('product.markup')}
                    title={t(`product.markupSource.${pr.markupSource}`)}
                    className="tnum h-6 cursor-pointer rounded border border-background-200 bg-background-50 px-1 text-[10.5px] text-foreground-800 outline-none focus:border-primary-400 disabled:cursor-default"
                  >
                    <option value="auto">{t('product.markupAuto', { value: pr.markupOverride == null ? pr.markup : '…' })}</option>
                    {markupChoices.map((m) => (
                      <option key={m} value={m}>
                        {m}%
                      </option>
                    ))}
                  </select>
                </span>
                <span className="tnum text-foreground-800">{pr.cost != null ? `+ ${aed(pr.cost * (pr.markup / 100))}` : '—'}</span>
              </div>
              <p className="text-[10px] leading-4 text-foreground-400">{t(`product.markupSource.${pr.markupSource}`)}</p>
              {optionIndex >= 0 && (product.options[optionIndex]?.surcharge ?? 0) > 0 && (
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-foreground-500">{product.options[optionIndex].label}</span>
                  <span className="tnum shrink-0 text-foreground-800">+ {aed(product.options[optionIndex].surcharge)}</span>
                </div>
              )}
              {pr.saleManual && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-foreground-500">{t('product.manualSite')}</span>
                  <span className="tnum text-accent-800">{pr.calcSale != null ? t('onb.calc', { value: aed(pr.calcSale) }) : ''}</span>
                </div>
              )}
              <div className="flex items-center justify-between border-t border-background-200 pt-2.5">
                <span className="text-[12.5px] font-semibold text-foreground-950">{t('product.salePrice')}</span>
                <span className="tnum text-lg font-semibold text-foreground-950">{price.sale != null ? aed(price.sale) : '—'}</span>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="rounded-md border border-primary-200 bg-primary-50 px-2.5 py-2">
                <p className="text-[9.5px] uppercase tracking-[0.08em] text-primary-800">{t('product.marginAed')}</p>
                <p className="tnum mt-0.5 text-[13px] font-semibold text-primary-900">{price.margin != null ? aed(price.margin) : '—'}</p>
              </div>
              <div className="rounded-md border border-background-200 bg-background-100 px-2.5 py-2">
                <p className="text-[9.5px] uppercase tracking-[0.08em] text-foreground-500">{t('product.marginPct')}</p>
                <p className="tnum mt-0.5 text-[13px] font-semibold text-foreground-950">{price.pct != null ? `${price.pct.toFixed(1)}%` : '—'}</p>
              </div>
            </div>

            <p className="mt-2 text-[10px] leading-4 text-foreground-400">{t('product.minResale', { rule: minResaleRule })}</p>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <h2 className="text-sm font-semibold text-foreground-950">{t('product.storefront')}</h2>
            <div className="mt-2 flex items-center justify-between gap-2 text-[11.5px]">
              <span className="text-foreground-500">{t('product.publishLabel')}</span>
              <StatusBadge tone={product.listing.status === 'live' ? 'primary' : product.listing.status === 'approved' ? 'secondary' : 'neutral'} dot={false}>
                {t(`onb.status.${product.listing.status}`, { defaultValue: product.listing.status })}
              </StatusBadge>
            </div>
            <p className="mt-1 text-[10.5px] leading-4 text-foreground-400">{t('product.publishDesc')}</p>
            {product.listing.previewUrl && (
              <Link to={product.listing.previewUrl} className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-background-300 py-1.5 text-[11.5px] font-medium text-foreground-700 hover:bg-background-100">
                <i className="ri-eye-line text-[12px]" /> {t('product.previewListing')}
              </Link>
            )}
            {product.listing.siteUrl && (
              <a href={product.listing.siteUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-primary-700 hover:text-primary-800">
                {t('product.openOnSite')} <i className="ri-external-link-line text-[11px]" />
              </a>
            )}
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <h2 className="text-sm font-semibold text-foreground-950">{t('product.usedIn')}</h2>
            <ul className="mt-2 space-y-2">
              {product.usedIn.map((s) => (
                <li key={s.id}>
                  <Link to={`/specifications/${s.id}`} className="flex items-center justify-between gap-2 rounded-md border border-background-200 px-2.5 py-1.5 transition-colors hover:bg-background-100">
                    <div className="min-w-0">
                      <p className="tnum text-[10.5px] text-foreground-500">
                        {s.code} · {t(`status.${s.status}`, { defaultValue: s.status })} · {s.qty} {t('common.units')}
                      </p>
                      <p className="truncate text-[11.5px] font-medium text-foreground-900">{s.project || s.client || '—'}</p>
                    </div>
                    <i className="ri-arrow-right-s-line shrink-0 text-[13px] text-foreground-300" />
                  </Link>
                </li>
              ))}
              {product.usedIn.length === 0 && <li className="text-[11.5px] text-foreground-500">{t('product.notUsedYet')}</li>}
            </ul>
          </div>

          {product.siteSales && (
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="text-sm font-semibold text-foreground-950">{t('product.siteSales')}</h2>
              {product.siteSales.unitsTotal === 0 ? (
                <p className="mt-2 text-[11.5px] text-foreground-500">{t('product.siteSalesNone')}</p>
              ) : (
                <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11.5px]">
                  <dt className="text-foreground-500">{t('product.siteSales30d')}</dt>
                  <dd className="tnum text-right text-foreground-900">{t('product.siteSalesUnitsOrders', { units: product.siteSales.units30d, orders: product.siteSales.orders30d })}</dd>
                  <dt className="text-foreground-500">{t('product.siteSalesRevenue30d')}</dt>
                  <dd className="tnum text-right text-foreground-900">{aed(product.siteSales.revenue30d, 0)}</dd>
                  <dt className="text-foreground-500">{t('product.siteSalesTotal')}</dt>
                  <dd className="tnum text-right text-foreground-900">{t('product.siteSalesUnitsOrders', { units: product.siteSales.unitsTotal, orders: product.siteSales.ordersTotal })}</dd>
                  <dt className="text-foreground-500">{t('product.siteSalesLast')}</dt>
                  <dd className="tnum text-right text-foreground-900">{product.siteSales.lastSoldAt ? shortDate(product.siteSales.lastSoldAt) : '—'}</dd>
                </dl>
              )}
            </div>
          )}
        </div>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/30 p-4" onClick={() => setShowAdd(false)}>
          <div className="w-full max-w-sm rounded-lg border border-background-200 bg-background-50 p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-sm font-semibold text-foreground-950">{t('product.addTitle')}</h2>
                <p className="mt-0.5 text-[11px] text-foreground-500">
                  {product.sku} · {product.name}
                </p>
              </div>
              <button type="button" onClick={() => setShowAdd(false)} aria-label={t('common.close')} className="cursor-pointer text-foreground-400 hover:text-foreground-700">
                <i className="ri-close-line text-base" />
              </button>
            </div>
            <label className="mt-4 block">
              <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('product.specification')}</span>
              <select value={specId} onChange={(e) => setSpecId(e.target.value)} className="h-9 w-full cursor-pointer rounded-md border border-background-200 bg-background-50 px-2 text-[12px] outline-none focus:border-primary-400">
                {specs.length === 0 && <option value="">{t('product.noOpenSpecs')}</option>}
                {specs.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.code} — {s.client || s.project || t(`status.${s.status}`)}
                  </option>
                ))}
              </select>
            </label>
            <label className="mt-3 block">
              <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('product.quantity')}</span>
              <input type="number" min={1} value={qty} onChange={(e) => setQty(Math.max(1, Number(e.target.value)))} className="tnum h-9 w-full rounded-md border border-background-200 bg-background-50 px-2.5 text-[12px] outline-none focus:border-primary-400" />
              <span className="mt-1 block text-[10.5px] text-foreground-400">
                {product.stock > 0
                  ? t('product.availableNow', { count: product.stock, source: product.source === 'Own' ? t('product.treejarWarehouse') : t('product.partnerWarehouse') })
                  : t('product.arrivingNote', { qty: product.incomingQty, date: product.incomingEta ? shortDate(product.incomingEta) : '—' })}
              </span>
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <Link to="/specifications" className="mr-auto inline-flex h-8 items-center text-[11.5px] font-semibold text-primary-700">
                {t('product.newSpecLink')}
              </Link>
              <button type="button" onClick={() => setShowAdd(false)} className="h-8 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-3 text-[12px] font-medium text-foreground-700 hover:bg-background-100">
                {t('common.cancel')}
              </button>
              <button type="button" disabled={!specId || busy} onClick={() => void addLine()} className="h-8 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 hover:bg-primary-600 disabled:cursor-default disabled:opacity-50">
                {t('product.addLine')}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} detail={toast.detail} tone={toast.tone} onClose={() => setToast(null)} />}
    </div>
  );
}
